import type { ReactNode } from 'react'

import { Chip, type TChipProps } from '@/src/elements/chip'
import { ICON } from '@/src/general/enums/icons'
import { getStrictDiffInDays } from '@/src/general/primitives/dates/dayjs'
import { useTranslate } from '@/src/lang/useTranslate'

import type { TMediumCommunityApi } from '../../../api/communityApi.types'
import type { TComputedSubscription } from '../../../utils/getComputedSubscription'

export type TCommunityDatesChipProps = {
  community: Pick<TMediumCommunityApi, 'fromDate' | 'toDate'>
  subscription: Pick<TComputedSubscription, 'fromDate' | 'toDate' | 'offset' | 'isPrayedToday'> | undefined
  short?: boolean
  className?: string
}

export const CommunityDatesChip = ({
  community,
  subscription,
  short,
  className,
}: TCommunityDatesChipProps): ReactNode => {
  const { t } = useTranslate()

  if (subscription?.isPrayedToday) return null

  /**
   * - If there is no subscription: use community dates
   * - If the community was begun on its first day: subscription and community dates are the same
   * - If the community was begun later from the start: use subscription dates
   * - If the community was joined mid-way:
   *   - subscription.toDate === community.toDate
   *   - subscription.fromDate > community.fromDate
   *   => use community dates
   */
  const entity = subscription && subscription.offset > 0 ? subscription : community

  if (!entity.fromDate || !entity.toDate) return null

  const today = new Date(Date.now()) // Use Date.now that can be mocked in tests
  const startDiffDays = Math.ceil(getStrictDiffInDays(today, entity.fromDate))
  const endDiffDays = entity.toDate && Math.ceil(getStrictDiffInDays(today, entity.toDate))
  const commonProps: TChipProps = { className, fontWeight: 'bold' }

  if (startDiffDays === 0)
    return <Chip text={t(short ? 'word.today' : 'common.word.start-today')} colors="primary" {...commonProps} />

  if (startDiffDays === 1)
    return (
      <Chip text={t(short ? 'common.word.tomorrow' : 'common.word.start-tomorrow')} colors="primary" {...commonProps} />
    )

  if (startDiffDays > 0)
    return (
      <Chip
        text={t(short ? 'words.in-nb-days' : 'words.in-nb-days-begin', { nbDays: startDiffDays })}
        colors="default"
        {...commonProps}
      />
    )
  if (endDiffDays < 0) return null

  return <Chip text={t('words.ongoing')} iconName={ICON.DOT} variant="flat" colors="danger" {...commonProps} />
}
